import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { Language, Market } from '../../common/CountryMarketConfig';
import { BgBubbles } from '../../components/BgBubbles';
import { Loading } from '../../components/Loading';
import { BriefingFlowTagController } from '../../controllers/BriefingFlowTagController';
import { useCountryMarketState } from '../../controllers/CountryMarketController/CountryMarketState';
import { getAvailableTaxonomiesFromState } from '../../controllers/TaxonomyController/TaxonomyHelper';
import { useTaxonomyState } from '../../controllers/TaxonomyController/TaxonomyState';
import { useAppRoutes } from '../../hooks/useAppRoutes';
import { useAuth } from '../../hooks/useAuth';
import { useBriefingRoute } from '../../hooks/useBriefingRoute';
import {
  getMandatoryApplications,
  processingCapabilitiesPotentiallyRequiredFields,
} from '../../lib/briefing';
import { MandatoryFieldDefinition, RouteParams } from '../../types';
import { getStepsByBranchByMarket } from './';
import './Briefing.css';
import { BriefingFooter } from './BriefingFooter';
import { isIncomplete } from './BriefingFooter/BriefingFooter';
import { BriefingHeader } from './BriefingHeader';
import { BriefingProgress } from './BriefingProgress';
import { useBriefingState } from './BriefingState';
import { isFieldAvailableDependingOnApplications } from './StepProcessingCapabilities/StepProcessingCapabilities';
import { processingCapabilitiesFields } from './StepProcessingCapabilities/fieldDependencies';

export const Briefing: FC = () => {
  const { briefingSelectionRoute } = useAppRoutes();
  const { branch, step } = useParams<RouteParams>();
  const [briefingState, { setBranchWithSteps }] = useBriefingState();
  const { productApplications, branch: stateBranch } = briefingState;
  const [taxonomyState] = useTaxonomyState();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [{ countryMarketConfig }] = useCountryMarketState();
  const briefingRoute = useBriefingRoute();
  const [{ user }] = useAuth();
  const [isValid, setIsValid] = useState(true);
  const userIsAzelisEmployee = useMemo(() => user?.azelis_employee, [user]);

  const availableTaxonomies = useMemo(
    () =>
      getAvailableTaxonomiesFromState(
        taxonomyState,
        countryMarketConfig,
        i18n.language as Language,
      ),
    [countryMarketConfig, i18n.language, taxonomyState],
  );

  const mandatoryFields = useMemo(() => {
    switch (countryMarketConfig?.marketDisplayName) {
      case Market.FoodNutrition:
        return {
          application: [
            getMandatoryApplications(
              productApplications,
              availableTaxonomies,
              countryMarketConfig,
            ),
          ],
          'formulation-attributes': ['productMarketingClaims', 'storage'],
          'processing-capabilities':
            productApplications.length > 0
              ? processingCapabilitiesPotentiallyRequiredFields
                  .map((field) =>
                    isFieldAvailableDependingOnApplications(
                      productApplications,
                      field,
                    )
                      ? field
                      : false,
                  )
                  .filter((field) => field)
              : processingCapabilitiesFields,
          price: [],
          'additional-info': [],
          problem: ['problem'],
          review: ['productName'],
          ingredients: [],
        } as MandatoryFieldDefinition;
      case Market.HomeCareIndustrialCleaning:
        return {
          application: [
            getMandatoryApplications(
              productApplications,
              availableTaxonomies,
              countryMarketConfig,
            ),
          ],
          'modification-type': ['modifyBriefingType'],
          'modification-issue': ['modifyBriefingIssue'],
          'formulation-attributes': ['productMarketingClaims', 'productForm'],
          requirements: [
            'requirementsAggressiveComponents',
            'requirementsLightStability',
          ],
          colour: ['colourShade', 'colourSamples', 'colourLabSupport'],
          fragrance: [
            'fragranceConcept',
            'fragranceDirection',
            'fragranceMaxAmountAllergens',
            'fragranceRequestedApplicationDosage',
            'fragranceSamples',
          ],
          price: [],
          'additional-info': [],
          'product-details': [
            'finishedProductSamples',
            'finishedProductPotential',
            'projectDeadline',
          ],
          review: ['productName'],
        } as MandatoryFieldDefinition;
      case Market.PersonalCare:
        return {
          application: [
            getMandatoryApplications(
              productApplications,
              availableTaxonomies,
              countryMarketConfig,
            ),
          ],
          'target-consumer': ['targetConsumerAge', 'targetConsumerGender'],
          'formulation-attributes': [
            'productMarketingClaims',
            'formulationType',
            'texture',
          ],
          restrictions: [],
          price: [],
          packaging: [],
          'additional-info': [],
          problem: ['problem'],
          review: ['productName'],
          ingredients: [],
        } as MandatoryFieldDefinition;
      case Market.AgriEnvironmental:
        return {
          application: [
            getMandatoryApplications(
              productApplications,
              availableTaxonomies,
              countryMarketConfig,
            ),
          ],
          'active-ingredients': ['activeIngredients'],
          'active-functions': ['activeFunctions'],
          'additional-info': [],
          problem: ['problem'],
          review: ['productName'],
          ingredients: [],
        } as MandatoryFieldDefinition;
      default:
        console.log('SOMETHING WENT WRONG GETTING MANDATORY FIELDS');
        return {};
    }
  }, [availableTaxonomies, countryMarketConfig, productApplications]);

  //  Specific useEffect to allow for the re-rendering of the steps
  //  if the branch is manually changed by the user and differs from the
  //  selected branch saved to state
  useEffect(() => {
    if (branch && branch !== stateBranch) {
      dispatch(
        setBranchWithSteps({
          branch,
          steps: getStepsByBranchByMarket(countryMarketConfig?.eLabMarketSlug)[
            branch
          ],
        }),
      );
    }
  }, [branch, stateBranch, setBranchWithSteps, countryMarketConfig, dispatch]);

  //  Specific useEffect to allow for the re-rendering of conditional steps
  //  for the Modify branch, currently only used for Home Care Industrial Cleaning market
  useEffect(() => {
    if (branch && branch === 'modify') {
      dispatch(
        setBranchWithSteps({
          branch,
          steps: getStepsByBranchByMarket(
            countryMarketConfig?.eLabMarketSlug,
            briefingState.modifyBriefingType,
          )[branch],
        }),
      );
    }
  }, [
    branch,
    setBranchWithSteps,
    countryMarketConfig,
    dispatch,
    briefingState.modifyBriefingType,
  ]);

  const Component = briefingRoute();

  return branch && step ? (
    <div className="flex flex-grow overflow-y-auto">
      <BgBubbles />
      <div className="fixed z-10 flex flex-col h-screen pl-3">
        <BriefingProgress
          step={step}
          isNavDisabled={isIncomplete(
            mandatoryFields[step],
            isValid,
            briefingState,
          )}
        />
      </div>
      {countryMarketConfig?.eLabMarketSlug === 'HomeCareIndustrialCleaning' && (
        <BriefingFlowTagController
          step={step}
          branch={branch}
          isEmployeeUser={userIsAzelisEmployee}
        />
      )}
      <div className="flex flex-col flex-grow p-3 pl-12 pr-6 md:pl-24">
        <BriefingHeader step={step} />
        <div className="relative flex-grow">
          {Component && Object.keys(availableTaxonomies).length > 0 ? (
            <div className="flex flex-grow briefing-step-enter-done">
              <Component
                mandatoryFields={mandatoryFields}
                onValidate={setIsValid}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loading text={t('labels:loading')} />
            </div>
          )}
        </div>
        <BriefingFooter
          step={step}
          mandatoryFields={mandatoryFields}
          isValid={isValid}
        />
      </div>
    </div>
  ) : (
    <Navigate to={briefingSelectionRoute} />
  );
};
